// src/auth/timker2Session.js
const TOKEN_KEY = "kbkk_timker2_token";
const USER_KEY = "kbkk_timker2_user";

const PROD_API_HTTPS = "https://apiklik.bkkpangkalpinang.com/api";
const PROD_API_HTTP = "http://apiklik.bkkpangkalpinang.com/api";

const trimEnd = (s = "") => String(s).replace(/\/+$/g, "");
const normalizeApiBase = (base = "") => {
  if (!base) return "";
  const clean = trimEnd(String(base).trim());
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};


export function normalizeTimker2WilkerName(value = "") {
  const text = String(value ?? "").trim().replace(/\s+/g, " ");
  const lower = text.toLowerCase();
  if (!text) return "";
  if (lower.includes("muntok")) return "Wilker Mentok";
  if (lower.includes("pos toboali") || lower.includes("toboali")) return "Pos Sadai";
  if (lower.includes("pelabuhan pangkal balam")) return "Wilker Pangkal Balam";
  if (lower.includes("wilker bandara depati amir")) return "Bandara Depati Amir";
  if (lower.includes("has hanandjoeddin")) return "Pos Bandara HAS Hanandjoeddin";
  return text;
}

export function normalizeTimker2SessionUser(user) {
  if (!user || typeof user !== "object") return user || null;
  const next = { ...user };
  ["timker2_wilker_nama", "wilker_nama", "wilayah_kerja", "wilker"].forEach((key) => {
    if (next[key]) next[key] = normalizeTimker2WilkerName(next[key]);
  });
  return next;
}

const maybeUpgradeToHttps = (base) => {
  const s = normalizeApiBase(base);
  if (typeof window === "undefined") return s;
  return window.location.protocol === "https:" && s.startsWith("http://")
    ? `https://${s.slice(7)}`
    : s;
};

export function makeTimker2ApiCandidates() {
  const out = [];
  const add = (base) => {
    if (!base) return;
    out.push(maybeUpgradeToHttps(base));
  };

  add(process.env.REACT_APP_API_BASE_URL);
  add(process.env.REACT_APP_API_PUBLIC_BASE);
  add(process.env.REACT_APP_API_BASE);
  add(process.env.VITE_API_BASE_URL);

  if (typeof window !== "undefined") {
    const { origin, port, hostname, protocol } = window.location;
    const isDevPort = ["3000", "5173", "4173"].includes(port || "");
    const isLocalHost = /^(localhost|127\.0\.0\.1|::1)$/i.test(hostname || "");

    if (
      hostname === "klik.bkkpangkalpinang.com" ||
      (typeof hostname === "string" && hostname.endsWith(".klik.bkkpangkalpinang.com"))
    ) {
      add(PROD_API_HTTPS);
      if (protocol === "http:") add(PROD_API_HTTP);
    }

    add(origin);
    add(`${origin}/public`);
    add(`${origin}/klik-bkk-api/public`);
    add(`${origin}/bkk-api/public`);

    if (isDevPort) {
      if (isLocalHost) {
        add("http://127.0.0.1:8000");
        add("http://localhost:8000");
      } else {
        add(`${protocol}//${hostname}:8000`);
      }
    }

    if (Array.isArray(window.__API_BASES__)) window.__API_BASES__.forEach(add);
    if (window.__ENV__?.API_BASE) add(window.__ENV__.API_BASE);
    if (window.__API_BASE) add(window.__API_BASE);
  }

  add(PROD_API_HTTPS);
  return Array.from(new Set(out.map(normalizeApiBase).filter(Boolean)));
}

export function getTimker2Session() {
  try {
    const token = localStorage.getItem(TOKEN_KEY) || "";
    const raw = localStorage.getItem(USER_KEY) || "";
    const user = raw ? normalizeTimker2SessionUser(JSON.parse(raw)) : null;
    return { token, user };
  } catch {
    return { token: "", user: null };
  }
}

export function setTimker2Session({ token, user }) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(normalizeTimker2SessionUser(user)));
  } catch {
    // ignore storage unavailable
  }
}

export function clearTimker2Session() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
}

export async function timker2Api(path, options = {}) {
  const { token } = getTimker2Session();
  const bases = makeTimker2ApiCandidates();
  let lastError = null;
  const cleanPath = String(path || "").startsWith("/") ? path : `/${path}`;

  for (const base of bases) {
    try {
      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${trimEnd(base)}${cleanPath}`, {
        ...options,
        headers,
        credentials: "omit",
        mode: "cors",
      });

      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text().catch(() => "");

      if (!res.ok) {
        lastError = { status: res.status, data };

        if (res.status === 423 || data?.code === "PASSWORD_CHANGE_REQUIRED") {
          try {
            const session = getTimker2Session();
            const lockedUser = {
              ...(session?.user || {}),
              must_change_password: true,
              force_password_change: true,
              password_notice: data?.password_notice || session?.user?.password_notice,
            };
            setTimker2Session({ user: lockedUser });
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("timker2-password-change-required", {
                detail: {
                  user: lockedUser,
                  password_notice: data?.password_notice,
                },
              }));
            }
          } catch {
            // ignore browser storage/event error
          }
          throw lastError;
        }

        if (res.status === 401 || res.status === 403) throw lastError;
        continue;
      }

      return { ok: true, data, base };
    } catch (error) {
      lastError = error;
      if (error?.status === 401 || error?.status === 403 || error?.status === 423) break;
    }
  }

  return { ok: false, error: lastError };
}

export async function timker2Logout() {
  await timker2Api("/timker2/auth/logout", { method: "POST" }).catch(() => null);
  clearTimker2Session();
}
